import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckBadgeIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import MyVendorCardActionMenu from "./MyVendorCardActionMenu";
import MyVendorLastQualifiedDateButton, {
  resolveLastQualifiedRequest,
} from "./MyVendorLastQualifiedDateButton";
import MyVendorTableRequestCell from "./MyVendorTableRequestCell";
import MyVendorTableTagsCell from "./MyVendorTableTagsCell";
import MyVendorsStickyScrollbar from "./MyVendorsStickyScrollbar";
import useMyVendorsStickyScrollbarSync from "./useMyVendorsStickyScrollbarSync";
import { MyVendorRiskScoreBadgeWithDots } from "./MyVendorRiskScoreBadge";
import ServiceCategoryPill from "../ServiceCategoryPill";
import { vendorTypeColors } from "../../utils/constants";
import {
  getVendorDisplayType,
  hasVendorRiskSummary,
  isManualOnlyVendor,
  LAST_QUALIFIED_DATE_COLUMN_ENABLED,
  RISK_SUMMARY_COLUMN_ENABLED,
} from "../../utils/myVendorsConstants";
import { sortVendorsByNameAscending } from "../../utils/myVendorSortUtils";
import { StickyActionsTd, StickyActionsTh } from "../../components/StickyActionsCells";
import {
  clickableRowClass,
  shouldIgnoreRowClick,
  stickyActionColumnStyle,
} from "../../utils/gridTableUx";
import GridOrgNameLink from "../common/GridOrgNameLink";
import { useGridOrgNameNavigation } from "../../custom-hooks/useGridOrgNameNavigation";
import { isMyVendorProfileVisible, myVendorOrgRow } from "../../utils/gridOrgNameLinks";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function parseLastQualifiedTimestamp(vendor) {
  const raw = vendor?.lastQualifiedRequest?.date || vendor?.lastQualifiedDate;
  if (!raw) return null;
  try {
    const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  } catch {
    return null;
  }
}

function compareStrings(a, b, direction) {
  const av = (a || "").toLowerCase();
  const bv = (b || "").toLowerCase();
  return direction * av.localeCompare(bv);
}

function compareNumbers(a, b, direction, zeroLast = false) {
  if (zeroLast) {
    if (a === 0 && b === 0) return 0;
    if (a === 0) return 1;
    if (b === 0) return -1;
  }
  if (a < b) return direction === 1 ? -1 : 1;
  if (a > b) return direction === 1 ? 1 : -1;
  return 0;
}

function GxpProfileCell({ applicableOnlyGxPs = [], demonstratedGxPs = [] }) {
  if (applicableOnlyGxPs.length === 0 && demonstratedGxPs.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {applicableOnlyGxPs.map((gxp) => (
        <span
          key={`applicable-${gxp}`}
          className="inline-flex items-center rounded-md bg-slate-200 px-2 py-1 text-xs font-medium text-slate-600"
        >
          {gxp}
        </span>
      ))}
      {applicableOnlyGxPs.length > 0 && demonstratedGxPs.length > 0 ? (
        <span className="mx-0.5 inline-block h-4 w-px bg-gray-300" aria-hidden="true" />
      ) : null}
      {demonstratedGxPs.map((gxp) => (
        <span
          key={`demonstrated-${gxp}`}
          className="inline-flex items-center rounded-md bg-slate-600 px-2 py-1 text-xs font-medium text-white"
        >
          {gxp}
        </span>
      ))}
    </div>
  );
}

/** Explicit widths so table-fixed distributes leftover space evenly after hidden columns. */
const COL = {
  name: "w-[180px] min-w-[180px]",
  type: "w-[150px] min-w-[150px]",
  location: "w-[150px] min-w-[150px]",
  riskSummary: "w-[150px] min-w-[150px]",
  gxp: "w-[130px] min-w-[130px]",
  services: "w-[180px] min-w-[180px]",
  libraryDocs: "w-[160px] min-w-[160px]",
  requests: "w-[280px] min-w-[280px]",
  lastQualified: "w-[140px] min-w-[140px]",
  tags: "w-[180px] min-w-[180px]",
};

function ServicesCell({ serviceCategories = [] }) {
  if (!serviceCategories.length) {
    return <span className="text-gray-400">—</span>;
  }

  const visible = serviceCategories.slice(0, 3);
  const overflow = serviceCategories.length - 3;

  return (
    <div className="flex min-w-0 w-full flex-wrap items-center gap-1">
      {visible.map((service) => (
        <ServiceCategoryPill key={service} name={service} />
      ))}
      {overflow > 0 ? (
        <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600">
          +{overflow} more
        </span>
      ) : null}
    </div>
  );
}

function LibraryDocumentsCell({ rfiCount = 0, assessmentCount = 0, hideVqq = false }) {
  return (
    <div className="space-y-1">
      {!hideVqq && (
        <div className="flex items-center">
          <CheckCircleIcon className="h-5 w-5 text-indigo-700" aria-hidden="true" />
          <span className="inline-block pl-2">
            {rfiCount} VQQ{rfiCount === 1 ? "" : "s"}
          </span>
        </div>
      )}
      <div className="flex items-center">
        <CheckBadgeIcon className="h-5 w-5 text-rose-700" aria-hidden="true" />
        <span className="inline-block pl-2">
          {assessmentCount} Audit{assessmentCount === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
}

/** My Vendors table view — flat vendor list, prototype column parity (Ticket 1.3). */
export default function MyVendorsListGrid({
  vendors = [],
  sponsorId = null,
  selectedVendorIds = new Set(),
  onToggleVendorSelection,
  allFilteredSelected = false,
  someFilteredSelected = false,
  onSelectAllFiltered,
  onVendorAction,
  onRequestAction,
  isBookmarked,
  isVendorActionPending,
  hideVqq = false,
}) {
  const navigate = useNavigate();
  const { navigateToHref, vendorHrefForRow } = useGridOrgNameNavigation();
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedRowIds, setExpandedRowIds] = useState(() => new Set());

  const checkboxRef = useRef(null);
  const tableContainerRef = useRef(null);
  const stickyScrollbarRef = useRef(null);
  const [isScrollbarSticky, setIsScrollbarSticky] = useState(true);

  const handleSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        if (sortDirection === "asc") {
          setSortDirection("desc");
        } else if (sortDirection === "desc") {
          setSortColumn(null);
          setSortDirection("asc");
        } else {
          setSortColumn(column);
          setSortDirection("asc");
        }
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn, sortDirection]
  );

  const toggleRowExpansion = useCallback((vendorId) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  }, []);

  const handleNavigateToProfile = useCallback(
    (vendor) => {
      if (isMyVendorProfileVisible(vendor)) navigate(`/provider/${vendor.id}/overview`);
    },
    [navigate]
  );

  const handleLastQualifiedClick = useCallback(
    (vendor) => {
      const fullRequest = resolveLastQualifiedRequest(vendor);
      if (!fullRequest) return;
      onRequestAction?.(fullRequest);
    },
    [onRequestAction]
  );

  const sortedVendors = useMemo(() => {
    if (!sortColumn) return sortVendorsByNameAscending(vendors);

    const direction = sortDirection === "desc" ? -1 : 1;
    const copy = [...vendors];

    copy.sort((a, b) => {
      switch (sortColumn) {
        case "name":
          return compareStrings(a.name, b.name, direction);
        case "riskSummary": {
          const av =
            hasVendorRiskSummary(a) && typeof a.riskSummary?.score === "number"
              ? a.riskSummary.score
              : -1;
          const bv =
            hasVendorRiskSummary(b) && typeof b.riskSummary?.score === "number"
              ? b.riskSummary.score
              : -1;
          if (av === -1 && bv === -1) return 0;
          if (av === -1) return 1;
          if (bv === -1) return -1;
          return direction * (av - bv);
        }
        case "type":
          return compareStrings(getVendorDisplayType(a.type), getVendorDisplayType(b.type), direction);
        case "location":
          return compareStrings(a.primaryLocation, b.primaryLocation, direction);
        case "myRequests": {
          const av = a.myRequests?.length || 0;
          const bv = b.myRequests?.length || 0;
          // Ascending = most requests first (prototype VendorListViewGrid parity).
          return compareNumbers(av, bv, direction === 1 ? -1 : 1, true);
        }
        case "libraryRequests": {
          const av = a.libraryRequests?.length || 0;
          const bv = b.libraryRequests?.length || 0;
          return compareNumbers(av, bv, direction === 1 ? -1 : 1, true);
        }
        case "lastQualified": {
          const av = parseLastQualifiedTimestamp(a);
          const bv = parseLastQualifiedTimestamp(b);
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return direction * (av - bv);
        }
        default:
          return 0;
      }
    });

    return copy;
  }, [vendors, sortColumn, sortDirection]);

  const headerChecked = allFilteredSelected;
  const headerIndeterminate = someFilteredSelected && !allFilteredSelected;

  useLayoutEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = headerIndeterminate;
    }
  }, [headerIndeterminate, headerChecked]);

  useMyVendorsStickyScrollbarSync(
    tableContainerRef,
    stickyScrollbarRef,
    vendors.length > 0,
    vendors.length
  );

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const tableContainer = tableContainerRef.current;
        if (!tableContainer) return;
        const rect = tableContainer.getBoundingClientRect();
        const tableBottom = rect.bottom;
        const windowHeight = window.innerHeight;
        if (isScrollbarSticky) {
          if (tableBottom <= windowHeight - 8) setIsScrollbarSticky(false);
        } else if (tableBottom > windowHeight + 100) {
          setIsScrollbarSticky(true);
        }
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [vendors, isScrollbarSticky]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ChevronUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="ml-1 h-4 w-4 text-indigo-600" />
    ) : (
      <ChevronDownIcon className="ml-1 h-4 w-4 text-indigo-600" />
    );
  };

  const SortHeader = ({ column, children, className = "" }) => (
    <th
      scope="col"
      className={classNames(
        "px-3 py-3.5 text-left text-sm font-semibold text-gray-900",
        className
      )}
    >
      <button
        type="button"
        className="inline-flex items-center group"
        onClick={() => handleSort(column)}
      >
        <span>{children}</span>
        {renderSortIcon(column)}
      </button>
    </th>
  );

  const StaticHeader = ({ children, className = "" }) => (
    <th
      scope="col"
      className={classNames(
        "px-3 py-3.5 text-left text-sm font-semibold text-gray-900",
        className
      )}
    >
      {children}
    </th>
  );

  if (!vendors.length) {
    return (
      <p className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        No vendors match the current search or filters.
      </p>
    );
  }

  return (
    <>
      <style>{`
        .my-vendors-table-hide-scrollbar::-webkit-scrollbar { display: none; }
        .my-vendors-table-hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="flow-root">
        <div className={isScrollbarSticky ? "" : "rounded-xl border border-gray-200 bg-white shadow-sm"}>
          <div
            ref={tableContainerRef}
            className={classNames(
              "my-vendors-table-hide-scrollbar overflow-x-scroll bg-white",
              isScrollbarSticky
                ? "mt-0 rounded-xl border border-gray-200 shadow-sm"
                : "rounded-t-xl border-b-0"
            )}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <div className="min-w-full py-0 align-middle">
              <table className="w-full min-w-[1780px] table-fixed divide-y divide-gray-200 [&_tbody_td]:h-px">
                <colgroup>
                  <col className="w-10" />
                  <col className={COL.name} />
                  <col className={COL.type} />
                  <col className={COL.location} />
                  {RISK_SUMMARY_COLUMN_ENABLED ? (
                    <col className={COL.riskSummary} />
                  ) : null}
                  <col className={COL.gxp} />
                  <col className={COL.services} />
                  <col className={COL.libraryDocs} />
                  <col className={COL.requests} />
                  <col className={COL.requests} />
                  {LAST_QUALIFIED_DATE_COLUMN_ENABLED ? (
                    <col className={COL.lastQualified} />
                  ) : null}
                  <col className={COL.tags} />
                  <col style={stickyActionColumnStyle()} />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="w-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <div className="group grid size-5 grid-cols-1">
                        <input
                          ref={checkboxRef}
                          type="checkbox"
                          className="col-start-1 row-start-1 size-5 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                          checked={headerChecked}
                          onChange={onSelectAllFiltered}
                        />
                        <svg
                          viewBox="0 0 16 16"
                          fill="white"
                          className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:checked]:opacity-100"
                        >
                          <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                        </svg>
                        <svg
                          viewBox="0 0 16 16"
                          fill="white"
                          className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:indeterminate]:opacity-100"
                        >
                          <path d="M4 8h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                    </th>
                    <SortHeader column="name" className={COL.name}>
                      Vendor
                    </SortHeader>
                    <SortHeader column="type" className={COL.type}>
                      Type
                    </SortHeader>
                    <SortHeader column="location" className={COL.location}>
                      Primary Location
                    </SortHeader>
                    {RISK_SUMMARY_COLUMN_ENABLED ? (
                      <SortHeader column="riskSummary" className={COL.riskSummary}>
                        Risk Summary
                      </SortHeader>
                    ) : null}
                    <StaticHeader className={COL.gxp}>GxP Profile</StaticHeader>
                    <StaticHeader className={COL.services}>Services</StaticHeader>
                    <StaticHeader className={COL.libraryDocs}>Library Documents</StaticHeader>
                    <SortHeader column="myRequests" className={COL.requests}>
                      My Requests
                    </SortHeader>
                    <SortHeader column="libraryRequests" className={COL.requests}>
                      Library Requests
                    </SortHeader>
                    {LAST_QUALIFIED_DATE_COLUMN_ENABLED ? (
                      <SortHeader column="lastQualified" className={COL.lastQualified}>
                        Last Qualified Date
                      </SortHeader>
                    ) : null}
                    <StaticHeader className={COL.tags}>Custom Tags</StaticHeader>
                    <StickyActionsTh tone="gray" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sortedVendors.map((vendor) => {
                    const vendorId = String(vendor.id);
                    const isSelected = selectedVendorIds.has(vendorId);
                    const isManual = isManualOnlyVendor(vendor);
                    const isRowExpanded = expandedRowIds.has(vendorId);
                    const applicableOnlyGxPs = (vendor.applicableGxPs || []).filter(
                      (gxp) => !(vendor.demonstratedGxPs || []).includes(gxp)
                    );
                    const demonstratedGxPs = vendor.demonstratedGxPs || [];
                    const serviceCategories =
                      vendor.serviceCategories?.length > 0
                        ? vendor.serviceCategories
                        : (vendor.provider?.provider_capabilities || [])
                            .map((cap) => cap.rfi_name || cap.name)
                            .filter(Boolean);

                    return (
                      <tr
                        key={vendorId}
                        className={clickableRowClass({
                          selected: isSelected,
                          interactive: isMyVendorProfileVisible(vendor),
                        })}
                        onClick={(e) => {
                          if (
                            shouldIgnoreRowClick(e.target)
                          ) {
                            return;
                          }
                          handleNavigateToProfile(vendor);
                        }}
                      >
                        <td
                          className={classNames(
                            "relative w-10 px-3 py-3 align-top",
                            isSelected ? "bg-indigo-50" : ""
                          )}
                        >
                          {isSelected ? (
                            <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
                          ) : null}
                          <input
                            type="checkbox"
                            className="size-5 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            checked={isSelected}
                            onChange={() => onToggleVendorSelection?.(vendor)}
                          />
                        </td>

                        <td
                          className={classNames(
                            COL.name,
                            "px-3 py-3 align-top text-sm"
                          )}
                        >
                          <GridOrgNameLink
                            name={vendor.name}
                            href={vendorHrefForRow(myVendorOrgRow(vendor))}
                            onNavigate={navigateToHref}
                            className="whitespace-normal text-sm font-semibold"
                          />
                        </td>

                        <td className={classNames(COL.type, "px-3 py-3 align-top text-sm text-gray-500")}>
                          <span className="flex items-start gap-2">
                            {vendor.type && vendorTypeColors[vendor.type] ? (
                              <span
                                className={classNames(
                                  "mt-1.5 h-2 w-2 flex-shrink-0 rounded-full",
                                  vendorTypeColors[vendor.type]
                                )}
                              />
                            ) : null}
                            <span className="min-w-0 whitespace-normal break-words">
                              {getVendorDisplayType(vendor.type)}
                            </span>
                          </span>
                        </td>

                        <td className={classNames(COL.location, "px-3 py-3 align-top text-sm text-gray-700")}>
                          {vendor.primaryLocation ? (
                            <span className="whitespace-normal break-words">{vendor.primaryLocation}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {RISK_SUMMARY_COLUMN_ENABLED ? (
                          <td className={classNames(COL.riskSummary, "px-3 py-3 align-top text-sm")}>
                            {!isManual && hasVendorRiskSummary(vendor) ? (
                              <MyVendorRiskScoreBadgeWithDots
                                score={vendor.riskSummary.score}
                                rating={vendor.riskSummary.rating}
                                size="sm"
                                showRatingText
                                showPercentage
                              />
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        ) : null}

                        <td className={classNames(COL.gxp, "px-3 py-3 align-top text-sm")}>
                          <GxpProfileCell
                            applicableOnlyGxPs={applicableOnlyGxPs}
                            demonstratedGxPs={demonstratedGxPs}
                          />
                        </td>

                        <td className={classNames(COL.services, "px-3 py-3 align-top text-sm")}>
                          <ServicesCell serviceCategories={serviceCategories} />
                        </td>

                        <td className={classNames(COL.libraryDocs, "px-3 py-3 align-top text-sm")}>
                          <LibraryDocumentsCell
                            rfiCount={vendor.rfiCount || 0}
                            assessmentCount={vendor.assessmentCount || 0}
                            hideVqq={hideVqq}
                          />
                        </td>

                        <td className={classNames(COL.requests, "px-3 py-3 align-top text-sm")}>
                          <MyVendorTableRequestCell
                            items={vendor.myRequests || []}
                            emptyLabel="No new requests"
                            onClickItem={onRequestAction}
                            expanded={isRowExpanded}
                            inverted={false}
                            onToggleExpand={() => toggleRowExpansion(vendorId)}
                          />
                        </td>

                        <td className={classNames(COL.requests, "px-3 py-3 align-top text-sm")}>
                          <MyVendorTableRequestCell
                            items={vendor.libraryRequests || []}
                            emptyLabel="No library requests"
                            onClickItem={onRequestAction}
                            expanded={isRowExpanded}
                            inverted
                            onToggleExpand={() => toggleRowExpansion(vendorId)}
                          />
                        </td>

                        {LAST_QUALIFIED_DATE_COLUMN_ENABLED ? (
                          <td className={classNames(COL.lastQualified, "px-3 py-3 align-top text-sm text-gray-700")}>
                            <MyVendorLastQualifiedDateButton
                              vendor={vendor}
                              onClick={handleLastQualifiedClick}
                            />
                          </td>
                        ) : null}

                        <td className={classNames(COL.tags, "px-3 py-3 align-top text-sm")}>
                          <MyVendorTableTagsCell providerId={vendor.id} sponsorId={sponsorId} />
                        </td>

                        <StickyActionsTd selected={isSelected}>
                          <MyVendorCardActionMenu
                            vendor={vendor}
                            onAction={onVendorAction}
                            isBookmarked={isBookmarked?.(vendor.id)}
                            isBookmarkPending={isVendorActionPending?.(vendor.id, "bookmark")}
                            isHidePending={isVendorActionPending?.(vendor.id, "hide")}
                            isUnhidePending={isVendorActionPending?.(vendor.id, "unhide")}
                          />
                        </StickyActionsTd>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <MyVendorsStickyScrollbar
          scrollRef={stickyScrollbarRef}
          isScrollbarSticky={isScrollbarSticky}
        />
      </div>
    </>
  );
}
